import React, { useRef } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import {
  FaMapMarkerAlt, FaBolt, FaCheckCircle, FaLock, FaMapMarkedAlt,
} from 'react-icons/fa';
import AgentTrustChip from '../components/AgentTrustChip';

const TRUST_POINTS = [
  { icon: <FaBolt size={14} />, label: 'Instant estimate' },
  { icon: <FaCheckCircle size={14} />, label: '100% free' },
  { icon: <FaLock size={14} />, label: 'No sign-up' },
  { icon: <FaMapMarkedAlt size={14} />, label: 'Local insights' },
];

function PropertySearchLiveDesktop({
  agentDetails, branding, selectedProperty, loadProperties,
  handlePropertyChange, handleSubmit, message, isSubmitting,
  showNoSignup, onContactAgent,
}) {
  const { accentColor, buttonTextColor, isLight } = branding;

  // Ref to the search pill so the portaled dropdown can match its width/left
  // edge (react-select otherwise sizes the menu to the inner control, which
  // excludes the leading location icon).
  const pillRef = useRef(null);

  const isBranded =
    agentDetails &&
    agentDetails.landing_page_slug &&
    agentDetails.landing_page_slug !== 'home-price' &&
    agentDetails.agent_name;

  const trustPoints = TRUST_POINTS.filter((t) => showNoSignup || t.label !== 'No sign-up');

  const selectStyles = {
    control: (p) => ({
      ...p, border: 'none', boxShadow: 'none', backgroundColor: 'transparent',
      minHeight: 46, cursor: 'text',
    }),
    valueContainer: (p) => ({ ...p, padding: '2px 4px' }),
    placeholder: (p) => ({ ...p, color: '#94a3b8', textAlign: 'left', fontSize: '1rem' }),
    input: (p) => ({ ...p, textAlign: 'left', color: '#0f172a' }),
    singleValue: (p) => ({ ...p, textAlign: 'left', fontWeight: 600, color: '#0f172a' }),
    menu: (p) => ({
      ...p, textAlign: 'left', borderRadius: 14, overflow: 'hidden',
      boxShadow: '0 12px 32px rgba(15,23,42,0.16)', marginTop: 10,
    }),
    menuPortal: (p) => {
      const rect = pillRef.current?.getBoundingClientRect();
      return {
        ...p,
        zIndex: 99999,
        ...(rect ? { left: rect.left, width: rect.width } : {}),
      };
    },
    option: (p, state) => ({
      ...p,
      backgroundColor: state.isFocused ? `${accentColor}12` : '#fff',
      color: '#0f172a', fontSize: '0.95rem', cursor: 'pointer',
      '&:active': { backgroundColor: `${accentColor}22` },
    }),
    dropdownIndicator: () => ({ display: 'none' }),
    indicatorSeparator: () => ({ display: 'none' }),
    loadingIndicator: (p) => ({ ...p, color: accentColor }),
  };

  return (
    <div
      className="psl-wrap psl-wrap--desktop"
      style={{
        '--psl-accent': accentColor,
        '--psl-accent-ring': `${accentColor}26`,
        '--psl-accent-soft': `${accentColor}14`,
      }}
    >
      <div className="psl-blob psl-blob--a" style={{ backgroundColor: accentColor }} aria-hidden="true" />
      <div className="psl-blob psl-blob--b" style={{ backgroundColor: accentColor }} aria-hidden="true" />

      <div className="psl-inner psl-inner--desktop">
        {!isBranded && <AgentTrustChip agentDetails={agentDetails} accentColor={accentColor} />}

        <h1 className="psl-title psl-title--desktop">
          What&rsquo;s your property<br />
          <span style={{ color: accentColor }}>worth today?</span>
        </h1>

        <p className="psl-sub psl-sub--desktop">
          Search your address for an instant, data-driven price estimate,
          powered by recent sales and local neighbourhood insights.{showNoSignup && ' Free, with no sign-up required.'}
        </p>

        <Form className="psl-searchcard" onSubmit={handleSubmit}>
          <div className="psl-search-pill" ref={pillRef}>
            <span className="psl-search-icon"><FaMapMarkerAlt size={18} /></span>
            <div className="psl-search-select">
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadProperties}
                onChange={handlePropertyChange}
                value={selectedProperty}
                noOptionsMessage={() => null}
                placeholder="Enter your property address…"
                openMenuOnClick={false}
                menuPortalTarget={document.body}
                styles={selectStyles}
                aria-label="Property address"
              />
            </div>
            <button
              type="submit"
              className="psl-btn"
              disabled={isSubmitting}
              style={{
                backgroundColor: accentColor,
                color: buttonTextColor,
                border: isLight ? '1px solid #222' : 'none',
                boxShadow: `0 6px 18px ${accentColor}33`,
              }}
            >
              {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Get my estimate'}
            </button>
          </div>
          {message && <div className="psl-msg">{message}</div>}
        </Form>

        <div className="psl-trust">
          {trustPoints.map((t, i) => (
            <span className="psl-trust-item" key={i}>{t.icon}{t.label}</span>
          ))}
        </div>

        {isBranded && (
          <div className="psl-agent">
            {agentDetails.agent_profile_image && (
              <img
                className="psl-agent-photo"
                src={agentDetails.agent_profile_image}
                alt={agentDetails.agent_name}
              />
            )}
            <div className="psl-agent-info">
              <div className="psl-agent-name">{agentDetails.agent_name}</div>
              <div className="psl-agent-agency">{agentDetails.agency_name_short}</div>
            </div>
            <button
              type="button"
              className="psl-agent-btn"
              onClick={onContactAgent}
              style={{ color: accentColor }}
            >
              Contact {agentDetails.agent_name?.split(' ')[0] || 'us'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PropertySearchLiveDesktop;
